import { historyWords as words } from "@/components/history/history-copy";
import { useEffect, useRef } from "react";
import type { Blocker, Part } from "@/components/ui/box.schema";
import { useImportTask } from "./useImportTask";
import type { ImportRowInput } from "@/lib/api/imports";
import type { InitialImportSource } from "./RunImportPanel";

export function useRunImportBox(initialSource?: InitialImportSource | null, composition?: { parts: Part[]; batches: Part[]; blockers: Blocker[]; rank?: number | null; keepOpen?: boolean }) {
  const task = useImportTask("history-import-course", words.bringInAClosedCourse, "historical", (source) => source
    ? <div className="grid gap-2 text-ui"><p>{source.name}</p><p>{source.rows.length} {words.sourceRowsThisImportGrantsNoPortalAccessAndCertifiesNoInferredOutcome}</p>
      {source.review && <dl className="grid gap-2">{Object.entries(source.review).map(([label, value]) => <div key={label}><dt>{label.replace(/([A-Z])/g, " $1")}</dt><dd>{value}</dd></div>)}</dl>}
      <details><summary>{words.readTheSourceEvidence}</summary><pre className="whitespace-pre-wrap break-all text-meta">{JSON.stringify(source.rows.map((r) => ({ kind: r.entity_kind, source: r.source_ref, record: r.data })), null, 2)}</pre></details></div>
    : <p>{words.readAnArchivedClassroomCourseAndCompleteItsInstitutionalRecordAbove}</p>, { ...composition, material: initialSource ? JSON.stringify([initialSource.sourceName, initialSource.payload]) : null });
  const previous = useRef(initialSource);
  const currentTask = useRef(task); currentTask.current = task;
  useEffect(() => {
    let active = true;
    if (!initialSource) {
      if (previous.current) currentTask.current.setSource(null);
      previous.current = initialSource;
      return;
    }
    previous.current = initialSource;
    void (async () => {
      try {
        const raw: unknown = JSON.parse(initialSource.payload);
        const rows = (Array.isArray(raw) ? raw : (raw as { rows: unknown }).rows) as ImportRowInput[];
        if (!Array.isArray(rows)) throw new Error(words.thisSourceHasNoRowList);
        const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(initialSource.payload));
        if (active) currentTask.current.setSource({ name: initialSource.sourceName, digest: Array.from(new Uint8Array(digest), (v) => v.toString(16).padStart(2, "0")).join(""), rows, problems: [], material: JSON.stringify([initialSource.sourceName, initialSource.payload]), scope: initialSource.key.replace(/:batch:\d+-of-\d+$/, ""), review: { ...initialSource.review } });
      } catch (e) {
        if (active) currentTask.current.setSource({ name: initialSource.sourceName, digest: "", rows: [], problems: [e instanceof Error ? e.message : words.thisSourceCouldNotBeRead] });
      }
    })();
    return () => { active = false; };
  }, [initialSource]);
  return task.box;
}
